import { OverlayModule } from '@angular/cdk/overlay';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { translate } from '@jsverse/transloco';
import { v4 as UUIDv4 } from 'uuid';
import { DropdownObject, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import {
  WidgetAnalysisDataSource,
  WidgetAnalysisGroupDataSource,
  WidgetKeyDriverAnalysisDataSource,
} from 'app/modules/widget/components/widget-data-source.component';
import {
  ComparisonFilterFieldExtra,
  ComparisonFilterSet,
} from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { SectionMetadata } from 'app/providers/storage-api/collection.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { SectionService } from 'app/services/section.service';
import { noWhitespaceValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { WidgetDataSource } from '../../../../models';
import { dynamicDateFilterValidatorFn } from '../comparison-filter-item/comparison-filter-dynamic-date-item/comparison-filter-dynamic-date-item.component';
import { ComparisonFilterItemComponent } from '../comparison-filter-item/comparison-filter-item.component';
import {
  ComparisonDateType,
  ComparisonFilterExtraDropdown,
  ComparisonSubType,
  ComparisonType,
} from '../create-comparison-drawer/create-comparison-drawer.component';
import { DatasourceSelectorComponent } from '../datasource-selector/datasource-selector.component';

import { DEFAULT_COMPARISON_COLOR } from '../../services/comparison-handler-service';

@EnableClassTelemetry()
@Component({
  selector: 'app-create-comparison-filter-dialog',
  imports: [
    AppSharedModule,
    OverlayModule,
    DatasourceSelectorComponent,
    ComparisonFilterItemComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './create-comparison-filter-dialog.component.html',
  styleUrls: ['./create-comparison-filter-dialog.component.scss'],
})
export class CreateComparisonFilterDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() options;

  @Output() modalClosed = new EventEmitter();

  typeTooltip = ``;

  comparisonFilterForm: FormGroup;

  isDataSourceSelectorVisible;
  dataSources: any[];
  selectedDataSource: WidgetDataSource;

  dataSourceFields: DropdownObject[] = [];
  groupFields;
  groupFieldSub$;
  filterLabelStatus: ErrorObject = ErrorObjectUtils.create();
  metadataFields: string[] = [];
  sectionMetadata: SectionMetadata[] = [];
  selectedFieldExtra: ComparisonFilterFieldExtra;
  subType: ComparisonSubType;
  dateType: ComparisonDateType;
  subDateType: ComparisonDateType;
  dynamicDateRangeEnabled: boolean = false;
  regularDateRangeEnabled: boolean = false;
  filtersExtraDropdown: ComparisonFilterExtraDropdown;
  isLoading: boolean;

  dateRangeFiltersCountFn = (subType: ComparisonSubType) =>
    subType === 'sectionDate' && this.options.allFilters?.length
      ? this.options.allFilters.filter((f) => !f?.dynamicDate)?.length
      : 0;
  hasDynamicDateFn = () => this.options.allFilters?.some((f) => !!f?.dynamicDate?.rangeNumber);

  filterValidatorFn = (control: AbstractControl<ComparisonFilterSet>): ValidationErrors | null => {
    if (!control.value?.and?.length && !control.value?.or?.length && !control.value?.not?.length) {
      return { required: true };
    }
  };

  existingFilterLabelFn = (control: AbstractControl<string>): ValidationErrors | null => {
    const findExistingLabels = this.options?.existingFilterLabels?.find(
      (i) => i.label === control.value
    );
    if (control.value && findExistingLabels) {
      return { duplicate: true };
    }
  };

  constructor(
    private accountService: StorageAccountService,
    private fb: FormBuilder,
    private customFieldService: CustomFieldsService,
    private sectionService: SectionService,
    private logger: LoggerService
  ) {
    this.comparisonFilterForm = this.fb.group({
      dataSource: [undefined, Validators.required],
      dataField: [undefined, Validators.required],
      filterLabel: [
        undefined,
        [
          Validators.required,
          Validators.maxLength(64),
          noWhitespaceValidator,
          this.existingFilterLabelFn,
        ],
      ],
      filterColor: [DEFAULT_COMPARISON_COLOR],
      filter: [undefined, this.filterValidatorFn],
    });
  }

  ngOnInit() {
    this.isLoading = true;
    this.groupFieldSub$ = this.accountService.groupFieldsSub.subscribe((accountMetadata) => {
      this.groupFields = accountMetadata.metadata
        .filter((f) => f.metadata?.display !== false)
        .map((f) => ({
          fieldName: `metadata.${f.fieldName}`,
          fieldType: f.fieldType,
          displayName: f.description,
          calculationFieldType: f.metadata?.calculationFieldType,
        }));
    });
    this.dateType = this.options.dateType;
    this.refreshFormValidators(this.dateType);

    this.typeTooltip = translate('create-comparison-filter-dialog-component.type-tooltip');
    this.comparisonFilterForm.controls.dataField.disable();
    this.filtersColorValue = this.options.activeFilterColor;

    this.comparisonFilterForm.controls.dataSource.valueChanges.subscribe(() => {
      this.comparisonFilterForm.controls.dataField.enable({ emitEvent: false });
    });

    this.comparisonFilterForm.controls.dataField.valueChanges.subscribe(() =>
      this.onDataFieldChanged()
    );

    this.comparisonFilterForm.statusChanges.pipe().subscribe(() => {
      const labelControl = this.comparisonFilterForm.controls.filterLabel;
      if (labelControl.errors) {
        const error = labelControl.errors;
        switch (Object.keys(error)[0]) {
          case 'duplicate':
            ErrorObjectUtils.setError(
              this.filterLabelStatus,
              translate('create-comparison-filter-dialog-component.filter-exists')
            );
            break;
          case 'maxlength':
            ErrorObjectUtils.setError(
              this.filterLabelStatus,
              translate('create-comparison-filter-dialog-component.max-length')
            );
            break;
          default:
            ErrorObjectUtils.setNoError(this.filterLabelStatus);
        }
      } else {
        ErrorObjectUtils.setNoError(this.filterLabelStatus);
      }
    });

    const flatDataSources = Object.values(this.options.dataSources).reduce(
      (prev: [], curr: []) =>
        prev.concat(
          ...curr.filter((ds: any) => !(ds instanceof WidgetKeyDriverAnalysisDataSource))
        ),
      []
    ) as (WidgetAnalysisDataSource | WidgetAnalysisGroupDataSource)[];

    let preselectedDataSource;

    if (this.options.selectedDataSourceId) {
      preselectedDataSource = flatDataSources.find(
        (ds) => ds.id === this.options.selectedDataSourceId
      );
    }

    if (!preselectedDataSource && Object.keys(this.options.dataSources).length === 1) {
      preselectedDataSource = flatDataSources[0];
    }

    this.onDataSourceSelected(preselectedDataSource);
  }

  ngOnDestroy(): void {
    this.groupFieldSub$.unsubscribe();
  }

  openDataSourceSelector($event) {
    $event.stopImmediatePropagation();

    this.isDataSourceSelectorVisible = true;
  }

  onDataFieldChanged() {
    this.onFiltersRemoved();

    if (this.isSectionFilterField) {
      this.onSectionDataFieldChanged();
    }
  }

  onSectionDataFieldChanged() {
    // Recalculate the selectedFieldExtra
    this.selectedFieldExtra = this.calculateSelectedFieldExtra();

    // Recalculate the subType
    this.subType = this.calculateSubType(this.options.type, this.selectedFieldExtra);

    // Recalculate relevant booleans
    this.dynamicDateRangeEnabled = this.dateRangeFiltersCountFn(this.subType) === 1;
    this.regularDateRangeEnabled =
      this.dateRangeFiltersCountFn(this.subType) === 0 || !this.hasDynamicDateFn();

    // Recalculate the subDateType
    this.subDateType = this.calculateSubDateType(
      this.subType,
      this.dynamicDateRangeEnabled,
      this.regularDateRangeEnabled
    );

    // Recalculate the dateType
    this.dateType = this.calculateDateType(this.subType);

    // Refresh the validators
    this.refreshFormValidators(this.dateType);

    // Set the filtersExtraDropdown
    this.filtersExtraDropdown = undefined;
    if (this.subType === 'sectionDate' && this.dynamicDateRangeEnabled) {
      this.filtersExtraDropdown = this.createFiltersExtraDropdown(
        undefined,
        this.dynamicDateRangeEnabled,
        this.regularDateRangeEnabled
      );
      this.filtersExtraDropdown.onValueChange = (value: ComparisonDateType) => {
        // If the value changes then update the dateType
        this.dateType = value;
        // And update the filtersExtraDropdown as well
        this.filtersExtraDropdown.value = value;
        // Refresh the validators
        this.refreshFormValidators(this.dateType);
      };
    }
  }

  createFiltersExtraDropdown(
    value: ComparisonDateType,
    dynamicDateRangeEnabled: boolean,
    regularDateRangeEnabled: boolean
  ) {
    return {
      placeholder: translate('create-comparison-drawer-component.type'),
      value,
      data: [
        {
          id: 'regularDate',
          title: translate('create-comparison-drawer-component.date-range'),
          tooltip: !regularDateRangeEnabled
            ? {
                text: translate('date-range-disabled-tooltip'),
              }
            : null,
        },
        {
          id: 'dynamicDate',
          title: translate('create-comparison-drawer-component.dynamic-date-range'),
          tooltip: !dynamicDateRangeEnabled
            ? {
                text: translate('dynamic-date-range-disabled-tooltip'),
              }
            : null,
        },
      ],
    };
  }

  calculateDateType(subType: ComparisonSubType): ComparisonDateType {
    // We should only set the value to the subDateType if the subType is sectionDate and the dynamicDateRangeEnabled is false
    if (subType === 'sectionDate') {
      return this.dynamicDateRangeEnabled ? undefined : this.subDateType;
    }
    return this.options.dateType;
  }

  calculateSubDateType(
    subType: ComparisonSubType,
    dynamicDateRangeEnabled: boolean,
    regularDateRangeEnabled: boolean
  ): ComparisonDateType {
    if (subType !== 'sectionDate') {
      return null;
    }

    if (this.filtersValue?.subDateType) {
      return this.filtersValue?.subDateType;
    }

    if (dynamicDateRangeEnabled) {
      return 'dynamicDate';
    } else if (regularDateRangeEnabled) {
      return 'regularDate';
    }
    return null;
  }

  refreshFormValidators(dateType: ComparisonDateType) {
    const filter = this.comparisonFilterForm.get('filter');
    if (dateType === 'dynamicDate') {
      filter.setValidators(dynamicDateFilterValidatorFn);
      return;
    }
    filter.setValidators(this.filterValidatorFn);
  }

  async onDataSourceSelected($event) {
    if (this.selectedDataSource === $event) {
      return;
    }
    this.selectedDataSource = $event;

    this.metadataFields = await this.getMetadataFields();
    this.sectionMetadata = await this.getSectionMetadata();
    this.dataSources = [{ id: $event.id, title: $event.displayName }];
    this.comparisonFilterForm.controls.dataSource.setValue(this.dataSources[0].id);

    this.dataSourceFields = this.fillDataSourceFields();

    this.comparisonFilterForm.controls.dataField.setValue(this.calculateInitialDataField());

    this.isDataSourceSelectorVisible = false;
    this.isLoading = false;
  }

  async getMetadataFields() {
    return await this.customFieldService.getMetadataSubTypesAsync(this.selectedDataSource);
  }

  private async getSectionMetadata(): Promise<SectionMetadata[]> {
    const result = [];
    if (!this.selectedDataSource.hasSections) {
      return result;
    }
    try {
      const sectionMetadataList = await this.sectionService.loadAllSectionMetadata(
        this.options.projectId,
        this.selectedDataSource
      );
      result.push(...sectionMetadataList);
    } catch (error) {
      this.logger.error('Failed to load section metadata', {
        error,
        dataSource: this.selectedDataSource,
      });
    }
    return result;
  }

  private fillDataSourceFields() {
    const fieldTypeMatcher = (fieldType, calculationFieldType) => {
      switch (this.options.type) {
        case 'date':
        case 'keyword':
          return fieldType === this.options.type;
        case 'numeric':
          return FieldService.hasNumerics(fieldType, calculationFieldType);
      }
    };

    const dataSourceFields = this.groupFields
      .filter(
        (f) =>
          fieldTypeMatcher(f.fieldType, f.calculationFieldType) &&
          this.metadataFields?.some((cf) => f.fieldName === `metadata.${cf}`)
      )
      .map((f) => ({ id: f.fieldName, title: f.displayName, type: f.fieldType }));

    if (this.options.type === 'date' && this.selectedDataSource.hasDateData) {
      dataSourceFields.push({
        id: 'createdAt',
        title: translate('create-comparison-filter-dialog-component.creation-date'),
      });
    }

    // Add section fields
    if (this.isSectionFilterField) {
      this.sectionMetadata.forEach((sm) => {
        dataSourceFields.push({
          id: sm.fieldName,
          title:
            sm.type === 'property'
              ? translate(`widgets.section-selector.${sm.fieldName}`)
              : FieldService.getFieldLongName(sm.fieldName),
        });
      });
    }

    return dataSourceFields;
  }

  private calculateInitialDataField() {
    let preselectedDataField = this.selectedField || this.options.selectedDataField;

    if (!this.dataSourceFields.some((dsf) => dsf.id === preselectedDataField)) {
      preselectedDataField = null;
    }

    if (!preselectedDataField && this.dataSourceFields.length === 1) {
      preselectedDataField = this.dataSourceFields[0].id;
    }

    return preselectedDataField;
  }

  calculateSelectedFieldExtra(): ComparisonFilterFieldExtra {
    if (!this.isSectionFilterField || !this.selectedField) {
      return undefined;
    }
    return {
      type: this.selectedField.startsWith(Constants.SECTION_PROPERTIES_PREFIX)
        ? FieldService.getSectionPropertyType(this.selectedField)
        : FieldService.getFieldType(this.selectedField),
      sectionMetadata: this.sectionMetadata,
    };
  }

  calculateSubType(
    type: ComparisonType,
    fieldExtra: ComparisonFilterFieldExtra
  ): ComparisonSubType {
    const selectedFieldType = fieldExtra?.type;
    // DASH-9694 for now we use subtypes for sectionFilters
    if (type !== 'sectionFilters' || !selectedFieldType) {
      return;
    }
    // Numerics
    if (FieldService.hasNumerics(selectedFieldType)) {
      return 'sectionNumeric';
    }
    // Dates
    if (FieldService.hasDate(selectedFieldType)) {
      return 'sectionDate';
    }
    // Keywords
    if (FieldService.hasKeyword(selectedFieldType)) {
      return 'sectionKeyword';
    }
  }

  get selectedField() {
    return this.comparisonFilterForm.controls.dataField.value;
  }

  get isSectionFilterField() {
    return this.options.type === 'sectionFilters';
  }

  get filtersLabelValue() {
    return this.comparisonFilterForm.controls.filterLabel.value;
  }

  set filtersLabelValue($event) {
    this.comparisonFilterForm.controls.filterLabel.setValue($event);
  }

  get filtersColorValue() {
    return this.comparisonFilterForm.controls.filterColor.value;
  }

  set filtersColorValue($event) {
    this.comparisonFilterForm.controls.filterColor.setValue($event);
  }

  get filtersValue() {
    return this.comparisonFilterForm.controls.filter.value;
  }

  set filtersValue($event) {
    this.comparisonFilterForm.controls.filter.setValue($event);
  }

  onFiltersRemoved() {
    this.filtersValue = {
      and: [],
      or: [],
      not: [],
      label: null,
      color: DEFAULT_COMPARISON_COLOR,
      id: null,
    };
  }

  cancel() {
    this.modalClosed.emit();
  }

  create() {
    this.filtersValue.id = UUIDv4();
    const filters: ComparisonFilterSet = this.filtersValue;
    filters.label = this.filtersLabelValue;
    filters.color = `rgba(${this.filtersColorValue.red},${this.filtersColorValue.green},${this.filtersColorValue.blue},${this.filtersColorValue.alpha})`;
    filters.subType = this.subType;
    filters.subDateType = this.subDateType;
    this.modalClosed.emit({
      dataSourceId: this.selectedDataSource.id,
      dataField: this.selectedField,
      filters,
      subType: this.subType,
      subDateType: this.subDateType,
    });
  }
}
